// The import that waits for Orquesti's answer to a limit request stays in the browser
// of the user (IndexedDB), not in Orquesti: no data of the people in the file is
// uploaded or stored by Orquesti while the request is pending
// (docs/import/usage-limits.md §5). It is what "Continuar importación" resumes.
//
// It lives as long as the user does not clear the browser data: it is deleted when the
// import completes and after SEVEN_DAYS. It only works on the same device and browser.
// Every access is best effort: a private window or blocked storage just means there is
// nothing to resume.

import type { ImportSourceType } from "@/context/importContext";

const DB_NAME = "orquesti-import";
const STORE = "pending";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export type PendingImportType = {
  companyId: number;
  /** Profile of the import: candidates or contracts. */
  profileId: "candidates" | "contracts";
  savedAt: number;
  /** The data as the user gave it and how its columns were matched. */
  source: ImportSourceType;
};

const open = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "companyId" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const run = async <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> => {
  try {
    const db = await open();
    return await new Promise<T>((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }).finally(() => db.close());
  } catch {
    return null;
  }
};

/** Keeps the import of the company, replacing the one it had. */
export const savePendingImport = async (
  pending: Omit<PendingImportType, "savedAt">,
): Promise<boolean> => {
  const saved = await run("readwrite", (store) =>
    store.put({
      ...pending,
      savedAt: Date.now(),
      source: { ...pending.source, isProcessing: false, fileError: null },
    } satisfies PendingImportType),
  );
  return saved !== null;
};

export const clearPendingImport = async (companyId: number): Promise<void> => {
  await run("readwrite", (store) => store.delete(companyId));
};

/** The import waiting for the company, unless it expired (then it is deleted). */
export const loadPendingImport = async (companyId: number): Promise<PendingImportType | null> => {
  const pending = await run<PendingImportType | undefined>("readonly", (store) => store.get(companyId));
  if (!pending) return null;
  if (Date.now() - pending.savedAt > SEVEN_DAYS) {
    await clearPendingImport(companyId);
    return null;
  }
  return pending;
};
